import React from "react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Strategic SEO Architecture",
  description: "We engineer your digital foundation to be prioritized by search algorithms. From semantic structure to technical metadata, we ensure your expertise is discoverable by the right audience at the right time.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605214/img/google_search_results_seo.png",
  alt: "Search engine optimization interface showing high-ranking technical metrics"
}, {
  title: "Persuasive Technical Copy",
  description: "Our content strategy bridges the gap between complex engineering and human trust. We craft professional narratives that articulate your value proposition while building immediate authority with stakeholders.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605214/img/professional_website_copywriting.png",
  alt: "Clean editorial layout featuring professional copywriting and typography"
}, {
  title: "Mobile-First Engineering",
  description: "With the majority of global traffic originating on mobile devices, we prioritize responsive performance. Your platform is optimized for precision and speed across every screen size and connectivity level.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605214/img/mobile_first_design_phone.png",
  alt: "High-performance mobile application interface on a smartphone screen"
}, {
  title: "Lead Capture Integration",
  description: "Transform passive visitors into active business opportunities. We implement seamless communication channels and conversion funnels that make professional engagement effortless and immediate.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605214/img/click_to_call_whatsapp_integration.png",
  alt: "Integrated communication dashboard with lead tracking functionality"
}, {
  title: "Managed Evolution",
  description: "The digital landscape is dynamic. We provide continuous maintenance and content optimization, ensuring your platform scales alongside your business objectives without technical friction.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605214/img/unlimited_website_updates.png",
  alt: "System management interface showing continuous deployment and updates"
}, {
  title: "Global Infrastructure",
  description: "Performance is a competitive advantage. We deploy your site on enterprise-grade secure servers designed for instant load times, robust security, and 99.9% uptime reliability.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1605214/img/fast_hosting_server_speed.png",
  alt: "Modern data center infrastructure representing secure and fast hosting"
}];
function FeatureRow({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const isEven = index % 2 === 0;
  return <div data-section-id="44671" ref={ref} data-index={index} className={cn("flex flex-col gap-12 md:gap-24 items-center transition-all duration-1000 ease-out", isEven ? "md:flex-row" : "md:flex-row-reverse", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")}>
      {}
      <div className="w-full md:w-1/2">
        <div className="relative aspect-video overflow-hidden rounded-[4px] shadow-none bg-muted">
          <img src={feature.image} alt={feature.alt} className="object-cover w-full h-full hover:scale-105 transition-transform duration-700" />
        </div>
      </div>

      {}
      <div className="w-full md:w-1/2 flex flex-col justify-center">
        <div className="max-w-lg mx-auto md:mx-0">
          <h3 className="text-2xl md:text-3xl font-bold tracking-tight text-[hsl(0,93%,49%)] mb-6 font-sans">
            {feature.title}
          </h3>
          <p className="text-[19px] text-foreground font-normal leading-[1.8] font-sans">
            {feature.description}
          </p>
        </div>
      </div>
    </div>;
}
export default function DoneForYouAllOfIt() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll();
  return <section id="strategic-insights" className="bg-background py-24 md:py-32 overflow-hidden">
      <div className="max-w-7xl mx-auto px-8">
        <div ref={headerRef} className={cn("text-center mb-24 transition-all duration-1000 ease-out", headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="text-[32px] md:text-[48px] font-bold tracking-tight text-[hsl(0,93%,49%)] mb-6 font-sans">
            Strategic Insights
          </h2>
          <p className="text-[19px] text-foreground font-normal leading-[1.8] max-w-2xl mx-auto font-sans">
            Comprehensive digital solutions engineered to elevate your technical authority and maximize local market dominance.
          </p>
        </div>

        <div className="space-y-24 md:space-y-32">
          {features.map((feature, i) => <FeatureRow key={i} feature={feature} index={i} />)}
        </div>
      </div>
    </section>;
}